import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Copy, KeyRound, Lock, LockOpen, RefreshCw } from "lucide-react"
import { type FormEvent, useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { useConfirmar } from "@/componentes/ui/Confirmar"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { comoFecha } from "@/lib/fechas"

import { PedirClave } from "./PedirClave"
import { BORRADO_MS, copiar } from "./portapapeles"

type PinDeCuenta = {
  cuenta_id: number
  nombre: string
  banco: string | null
  activa: boolean
  tiene_pin: boolean
  credencial_id: number | null
  ultimo_cambio: string | null
}

/** Lo que se pide al servidor: fijar uno (con `pin`), generarlo (sin) o quitarlo. */
type Pedido =
  | { accion: "fijar"; cuenta: PinDeCuenta; pin: string | null }
  | { accion: "quitar"; cuenta: PinDeCuenta }

/**
 * El PIN de cada cuenta bancaria: generarlo, fijarlo y quitarlo (pedido el 09/10).
 *
 * ⚠ **Con el cofre abierto.** Cualquier cambio pide tu contraseña del sistema si el
 * cofre está cerrado, igual que revelar una credencial; lo decide el servidor
 * (`services/pin_bancos.py`) y aquí sólo se pregunta cuando contesta `COFRE_CERRADO`.
 *
 * ⚠ **El PIN se enseña UNA vez**, al fijarlo o generarlo. Después se consulta en el
 * cofre, en la categoría «PIN de cuentas bancarias», revelándolo como cualquier
 * credencial — con su bitácora.
 *
 * ⚠ **Cambiarlo cierra la cuenta a quien la tuviera abierta.** Se avisa antes.
 */
export function PinesBancarios({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const [confirmacion, confirmar] = useConfirmar()
  const [pedido, setPedido] = useState<Pedido | null>(null)
  const [fijando, setFijando] = useState<PinDeCuenta | null>(null)
  const [pidiendoClave, setPidiendoClave] = useState(false)
  const [nuevo, setNuevo] = useState<{ cuenta: string; pin: string } | null>(null)

  const lista = useQuery({
    queryKey: ["pines-bancarios"],
    queryFn: () => api.get<PinDeCuenta[]>("/api/v1/pines-bancarios"),
    enabled: abierto,
  })

  const enviar = useMutation({
    mutationFn: async ({ p, password }: { p: Pedido; password?: string }) => {
      if (p.accion === "quitar") {
        await api.post(`/api/v1/pines-bancarios/${p.cuenta.cuenta_id}/quitar`, { password })
        return null
      }
      return api.post<{ cuenta_id: number; pin: string }>(
        `/api/v1/pines-bancarios/${p.cuenta.cuenta_id}`,
        { ...(p.pin ? { pin: p.pin } : {}), password },
      )
    },
    onSuccess: (r, { p }) => {
      setPidiendoClave(false)
      setPedido(null)
      setFijando(null)
      if (r) setNuevo({ cuenta: p.cuenta.nombre, pin: r.pin })
      for (const clave of ["pines-bancarios", "credenciales", "cuentas-bancarias"])
        void clienteQuery.invalidateQueries({ queryKey: [clave] })
      void clienteQuery.invalidateQueries({ queryKey: ["estado-cuenta"] })
      void clienteQuery.invalidateQueries({ queryKey: ["resumen-cuenta"] })
    },
    onError: (e) => {
      if (e instanceof ErrorApi && e.detalles?.motivo === "COFRE_CERRADO") setPidiendoClave(true)
    },
  })

  function pedir(p: Pedido) {
    setPedido(p)
    enviar.mutate({ p })
  }

  const error =
    enviar.error instanceof ErrorApi && enviar.error.detalles?.motivo !== "COFRE_CERRADO"
      ? enviar.error.message
      : null

  return (
    <>
      <Modal
        abierto={abierto}
        titulo="PIN de cuentas bancarias"
        descripcion="El PIN que pide cada cuenta para ver su saldo, su extracto y pagar desde ella. Cambiarlo pide tu contraseña del sistema y cierra la cuenta a quien la tuviera abierta."
        onCerrar={onCerrar}
        ancho="lg"
      >
        {lista.isPending ? (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Cargando…
          </p>
        ) : lista.isError ? (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {lista.error.message}
          </p>
        ) : lista.data.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            No hay cuentas registradas en Tesorería.
          </p>
        ) : (
          <ul className="grid gap-2">
            {lista.data.map((c) => (
              <li
                key={c.cuenta_id}
                className="tarjeta flex flex-wrap items-center gap-2 p-3"
                data-testid={`pin-cuenta-${c.cuenta_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {c.nombre}
                    {!c.activa && <span style={{ color: "var(--fg-muted)" }}> · inactiva</span>}
                  </p>
                  <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {c.banco ?? "Sin banco"}
                    {c.ultimo_cambio && ` · cambiado el ${comoFecha(c.ultimo_cambio)}`}
                    {c.tiene_pin && c.credencial_id === null && " · sin copia en el cofre"}
                  </p>
                </div>
                {c.tiene_pin ? (
                  <Insignia tono="exito" icono={<Lock size={12} aria-hidden={true} />}>
                    Con PIN
                  </Insignia>
                ) : (
                  <Insignia icono={<LockOpen size={12} aria-hidden={true} />}>Sin PIN</Insignia>
                )}
                <div className="flex flex-wrap gap-1">
                  <Boton
                    tamano="sm"
                    disabled={enviar.isPending}
                    onClick={() => pedir({ accion: "fijar", cuenta: c, pin: null })}
                  >
                    <RefreshCw size={14} aria-hidden={true} />
                    {c.tiene_pin ? "Generar otro" : "Generar"}
                  </Boton>
                  <Boton tamano="sm" disabled={enviar.isPending} onClick={() => setFijando(c)}>
                    <KeyRound size={14} aria-hidden={true} />
                    Escribir uno
                  </Boton>
                  {c.tiene_pin && (
                    <Boton
                      tamano="sm"
                      variante="peligro"
                      disabled={enviar.isPending}
                      onClick={async () => {
                        const si = await confirmar({
                          titulo: "Quitar el PIN",
                          mensaje: `¿Quitar el PIN de ${c.nombre}? La cuenta se abrirá sin él, y su copia sale del cofre.`,
                          aceptar: "Quitar el PIN",
                          peligro: true,
                        })
                        if (si) pedir({ accion: "quitar", cuenta: c })
                      }}
                    >
                      Quitar
                    </Boton>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            {error}
          </p>
        )}
        <p className="mt-3 text-xs" style={{ color: "var(--fg-muted)" }}>
          Para consultar un PIN, revélalo en el cofre, en la categoría «PIN de cuentas bancarias».
        </p>
      </Modal>

      <EscribirPin
        cuenta={fijando}
        pendiente={enviar.isPending}
        onCerrar={() => setFijando(null)}
        onEnviar={(pin) => fijando && pedir({ accion: "fijar", cuenta: fijando, pin })}
      />

      <PedirClave
        abierto={pidiendoClave}
        error={enviar.error}
        pendiente={enviar.isPending}
        onEnviar={(password) => pedido && enviar.mutate({ p: pedido, password })}
        onCerrar={() => {
          setPidiendoClave(false)
          enviar.reset()
        }}
      />

      <PinNuevo nuevo={nuevo} onCerrar={() => setNuevo(null)} />
      {confirmacion}
    </>
  )
}

function EscribirPin({
  cuenta,
  pendiente,
  onEnviar,
  onCerrar,
}: {
  cuenta: { nombre: string } | null
  pendiente: boolean
  onEnviar: (pin: string) => void
  onCerrar: () => void
}) {
  const id = useId()
  const [pin, setPin] = useState("")
  const valido = pin.trim().length >= 4 && pin.trim().length <= 32

  function enviar(e: FormEvent) {
    e.preventDefault()
    if (valido) onEnviar(pin.trim())
  }

  return (
    <Modal
      abierto={cuenta !== null}
      titulo={`PIN de ${cuenta?.nombre ?? ""}`}
      descripcion="Entre 4 y 32 caracteres. Mejor seis cifras o más."
      onCerrar={() => {
        setPin("")
        onCerrar()
      }}
      ancho="sm"
    >
      <form onSubmit={enviar}>
        <label htmlFor={`${id}-pin`} className="mb-1 block text-xs font-medium uppercase">
          Nuevo PIN
        </label>
        <input
          id={`${id}-pin`}
          type="password"
          autoComplete="new-password"
          value={pin}
          maxLength={32}
          onChange={(e) => setPin(e.target.value)}
          className="campo w-full tabular-nums"
        />
        <div className="mt-4 flex justify-end">
          <Boton variante="primario" type="submit" disabled={!valido || pendiente}>
            Guardar PIN
          </Boton>
        </div>
      </form>
    </Modal>
  )
}

function PinNuevo({
  nuevo,
  onCerrar,
}: {
  nuevo: { cuenta: string; pin: string } | null
  onCerrar: () => void
}) {
  const [copiado, setCopiado] = useState(false)
  return (
    <Modal
      abierto={nuevo !== null}
      titulo={`PIN de ${nuevo?.cuenta ?? ""}`}
      descripcion="Se enseña sólo ahora. Queda guardado cifrado en el cofre, en «PIN de cuentas bancarias»."
      onCerrar={() => {
        setCopiado(false)
        onCerrar()
      }}
      ancho="sm"
    >
      <p
        className="tabular-nums select-all text-center text-2xl font-semibold tracking-[0.3em]"
        data-testid="pin-nuevo"
      >
        {nuevo?.pin}
      </p>
      <div className="mt-4 flex flex-col items-center gap-2">
        <Boton
          onClick={async () => {
            if (nuevo) setCopiado(await copiar(nuevo.pin, true))
          }}
        >
          <Copy size={14} aria-hidden={true} />
          Copiar
        </Boton>
        {copiado && (
          <p role="status" className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Copiado. El portapapeles se borra en {BORRADO_MS / 1000} segundos.
          </p>
        )}
      </div>
    </Modal>
  )
}
